const createShareCodeString = (value) => {
  if (!value) {
    return "_";
  }
  // We use '~' as escape symbol and '_' as terminator.
  return `${value.replaceAll("~", "~~").replaceAll("_", "~_")}_`;
};

const createShareCodeItem = (item) => {
  let code = `${item.weight}_${item.height}_${item.length}_${item.width}_${item.quantity}_`;
  code += createShareCodeString(item.presetSize);
  code += item.unitSystem === "metric" ? "m" : "i";
  const settingsPalletized = item.settingsPalletized ? 2 : 0;
  const settingsNonStackable = item.settingsNonStackable ? 1 : 0;
  code += settingsPalletized + settingsNonStackable;
  return code;
};

const createShareCode = (shareDataFromProps) => {
  let code =
    shareDataFromProps.spotShipmentPage.selectedSegment === "private"
      ? "p"
      : "b";
  code +=
    shareDataFromProps.shipmentLane.selectedUnitSystem === "metric" ? "m" : "i";
  const items = shareDataFromProps.shipmentLane.initialSet.items || [];
  code = items.reduce(
    (accu, item) => accu + createShareCodeItem(item),
    `${code}${items.length}_`
  );
  code += shareDataFromProps.shipmentLane.initialSet.settingsDangerousGoods
    ? "1"
    : "0";
  const businessDoc = shareDataFromProps.tradeLane.documentsOptionValue.business
    ? 2
    : 0;
  const privateDoc = shareDataFromProps.tradeLane.documentsOptionValue.private
    ? 1
    : 0;
  code += businessDoc + privateDoc;
  code += shareDataFromProps.tradeLane.originDestinationFlipped ? "1" : "0";
  const tradeSet = shareDataFromProps.tradeLane.initialSet;
  code += createShareCodeString(tradeSet.destinationCity);
  code += createShareCodeString(tradeSet.destinationCitydisplayValue);
  code += createShareCodeString(tradeSet.destinationCountry);
  code += tradeSet.destinationUseFallback ? "1" : "0";
  code += createShareCodeString(tradeSet.destinationZip);
  code += createShareCodeString(tradeSet.destinationZipdisplayValue);
  code += createShareCodeString(tradeSet.originCity);
  code += createShareCodeString(tradeSet.originCitydisplayValue);
  code += createShareCodeString(tradeSet.originCountry);
  code += tradeSet.originUseFallback ? "1" : "0";
  code += createShareCodeString(tradeSet.originZip);
  code += createShareCodeString(tradeSet.originZipdisplayValue);
  code += tradeSet.receiverAddressType === "RESIDENTIAL" ? "r" : "b";
  code += tradeSet.receiverType === "CONSUMER" ? "c" : "b";
  code += tradeSet.senderType === "CONSUMER" ? "c" : "b";
  switch (shareDataFromProps.getQuote.sortOffersBy) {
    case "offersSortPriceHighToLow":
      code += "h";
      break;
    case "offersSortPriceLowToHigh":
      code += "l";
      break;
    default:
      // offersSortDeliveryFastToSlow
      code += "f";
      break;
  }
  return `.${encodeURIComponent(code)}`;
};

const createShareUrl = (encodedShareCode) => {
  let currentUrl = "";
  currentUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
  return `${currentUrl}?share=${encodedShareCode}`;
};

class ShareCodeParseBuffer {
  constructor(code) {
    this.code = code;
  }

  chop(length) {
    this.code = this.code.substring(length);
  }

  parseString() {
    let decodedValue = "";
    let start = 0;
    for (;;) {
      const escapeIndex = this.code.indexOf("~", start);
      const terminatorIndex = this.code.indexOf("_", start);
      if (escapeIndex < 0) {
        if (terminatorIndex < 0) {
          decodedValue += this.code.substring(start);
          this.code = "";
          return decodedValue;
        }
        decodedValue += this.code.substring(start, terminatorIndex);
        this.chop(terminatorIndex + 1);
        return decodedValue;
      }
      if (terminatorIndex >= 0 && terminatorIndex < escapeIndex) {
        decodedValue += this.code.substring(start, terminatorIndex);
        this.chop(terminatorIndex + 1);
        return decodedValue;
      }
      if (escapeIndex + 1 >= this.code.length) {
        decodedValue += "~";
        this.code = "";
        return decodedValue;
      }
      decodedValue +=
        this.code.substring(start, escapeIndex) + this.code[escapeIndex + 1];
      start = escapeIndex + 2;
    }
  }

  parseAtom(transform) {
    const terminatorIndex = this.code.indexOf("_");
    if (terminatorIndex < 0) {
      const decodedValue = transform(this.code);
      this.code = "";
      return decodedValue;
    }
    const decodedValue = transform(this.code.substring(0, terminatorIndex));
    this.chop(terminatorIndex + 1);
    return decodedValue;
  }

  parseEnum(transform) {
    if (this.code.length === 0) {
      return transform("");
    }
    const decodedValue = transform(this.code[0]);
    this.chop(1);
    return decodedValue;
  }
}

const transformShareCodeUnitSystem = (letter) =>
  letter === "i" ? "imperial" : "metric";
const transformShareCodeBoolean = (letter) => letter !== "0";
const transformShareCustomerType = (letter) =>
  letter === "b" ? "BUSINESS" : "CONSUMER";
const transformShareFloatAsString = (code) => String(parseFloat(code));

const parseShareCodeItem = (buffer) => {
  const weight = buffer.parseAtom(transformShareFloatAsString);
  const height = buffer.parseAtom(transformShareFloatAsString);
  const length = buffer.parseAtom(transformShareFloatAsString);
  const width = buffer.parseAtom(transformShareFloatAsString);
  const quantity = buffer.parseAtom(parseInt);
  const presetSize = buffer.parseString();
  const unitSystem = buffer.parseEnum(transformShareCodeUnitSystem);
  const flags = buffer.parseEnum(parseInt);
  const settingsPalletized = Math.floor(flags / 2) > 0;
  const settingsNonStackable = flags % 2 > 0;
  return {
    weight,
    height,
    length,
    width,
    quantity,
    presetSize,
    unitSystem,
    settingsPalletized,
    settingsNonStackable,
  };
};

const parseShareCode = (code) => {
  const buffer = new ShareCodeParseBuffer(code);
  const state = {};
  // spotShipmentPage
  const selectedSegment = buffer.parseEnum((letter) =>
    letter === "b" ? "business" : "private"
  );
  state.spotShipmentPage = { selectedSegment };
  // shipmentLane
  const selectedUnitSystem = buffer.parseEnum(transformShareCodeUnitSystem);
  const itemCount = buffer.parseAtom(parseInt);
  const items = [];
  for (let itemIndex = 0; itemIndex < itemCount; itemIndex += 1) {
    const item = parseShareCodeItem(buffer);
    items.push(item);
  }
  const settingsDangerousGoods = buffer.parseEnum(transformShareCodeBoolean);
  let initialSet = { items, settingsDangerousGoods };
  state.shipmentLane = { selectedUnitSystem, initialSet };
  // tradeLane
  const flags = buffer.parseEnum(parseInt);
  const documentsOptionValue = {
    business: Math.floor(flags / 2) > 0,
    private: flags % 2 > 0,
  };
  const originDestinationFlipped = buffer.parseEnum(transformShareCodeBoolean);
  const destinationCity = buffer.parseString();
  const destinationCitydisplayValue = buffer.parseString();
  const destinationCountry = buffer.parseString();
  const destinationUseFallback = buffer.parseEnum(transformShareCodeBoolean);
  const destinationZip = buffer.parseString();
  const destinationZipdisplayValue = buffer.parseString();
  const originCity = buffer.parseString();
  const originCitydisplayValue = buffer.parseString();
  const originCountry = buffer.parseString();
  const originUseFallback = buffer.parseEnum(transformShareCodeBoolean);
  const originZip = buffer.parseString();
  const originZipdisplayValue = buffer.parseString();
  const receiverAddressType = buffer.parseEnum((letter) =>
    letter === "b" ? "BUSINESS" : "RESIDENTIAL"
  );
  const receiverType = buffer.parseEnum(transformShareCustomerType);
  const senderType = buffer.parseEnum(transformShareCustomerType);
  initialSet = {
    destinationCity,
    destinationCitydisplayValue,
    destinationCountry,
    destinationUseFallback,
    destinationZip,
    destinationZipdisplayValue,
    originCity,
    originCitydisplayValue,
    originCountry,
    originUseFallback,
    originZip,
    originZipdisplayValue,
    receiverAddressType,
    receiverType,
    senderType,
  };
  state.tradeLane = {
    documentsOptionValue,
    originDestinationFlipped,
    initialSet,
  };
  // getQuote
  const sortOffersBy = buffer.parseEnum((letter) => {
    switch (letter) {
      case "h":
        return "offersSortPriceHighToLow";
      case "l":
        return "offersSortPriceLowToHigh";
      default:
        // 'f'
        return "offersSortDeliveryFastToSlow";
    }
  });
  state.getQuote = { sortOffersBy };
  return state;
};

export { createShareCode, createShareUrl, parseShareCode };
